import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    ReactNode,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';

/**
 * Account-visibility context (named "HiddenAccounts" for backwards compat with
 * earlier imports — the model is broader now).
 *
 * Visibility rule
 * ───────────────
 * Each account key resolves to one of three states:
 *
 *   • "hide"   — user explicitly removed it from the Dashboard
 *   • "show"   — user explicitly added it to the Dashboard
 *   • undefined (default) — show on Dashboard iff the balance is non-zero
 *
 * Why this model
 * ──────────────
 * Zero-balance current accounts are noise on the Dashboard, but the user must
 * still be able to opt them in (e.g. they're holding it intentionally). A
 * binary hidden/visible flag can't express "show this even though the default
 * would hide it", so we use an explicit override map instead.
 *
 * Identification key format
 * ─────────────────────────
 * Current accounts group by token_symbol on the Dashboard, so the natural
 * key is the uppercase symbol — `keyForCurrent('usdt') → 'current:USDT'`.
 * Deposits are per account — `keyForDeposit(accountId) → 'deposit:<uuid>'`.
 *
 * Persistence
 * ───────────
 * Per user: AsyncStorage under '@oneapp/account_visibility:v3:<user id>' as
 * JSON `{ [key]: 'show' | 'hide' }`. The map used to be one for the whole
 * device — and current accounts are keyed by symbol — so USDT hidden by one
 * user was hidden for whoever signed in next. Signed out, the map is empty
 * and nothing is stored.
 *
 * Migration: the device-wide map (v2 '@oneapp/account_visibility:v2', or the
 * older v1 '@oneapp/hidden_current_accounts:v1', a string[] of hidden keys)
 * goes once to the first user who signs in without a map of their own, and
 * is then removed.
 */

const storageKeyFor = (userId: string): string => `@oneapp/account_visibility:v3:${userId}`;
const LEGACY_KEY_V2 = '@oneapp/account_visibility:v2';
const LEGACY_KEY_V1 = '@oneapp/hidden_current_accounts:v1';

/** A stored v2/v3 map, cleaned; anything unreadable → null. */
const parseOverrides = (raw: string | null): Record<string, VisibilityOverride> | null => {
    if (!raw) { return null; }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { return null; }
        const cleaned: Record<string, VisibilityOverride> = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (v === 'show' || v === 'hide') { cleaned[k] = v; }
        }
        return cleaned;
    } catch {
        return null;
    }
};

/** The legacy v1 list of hidden keys as a map, or null. */
const parseLegacyV1 = (raw: string | null): Record<string, VisibilityOverride> | null => {
    if (!raw) { return null; }
    try {
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) { return null; }
        const migrated: Record<string, VisibilityOverride> = {};
        for (const k of arr) {
            if (typeof k === 'string') { migrated[k] = 'hide'; }
        }
        return migrated;
    } catch {
        return null;
    }
};

export type VisibilityOverride = 'show' | 'hide';

export const keyForCurrent = (tokenSymbol: string): string =>
    `current:${(tokenSymbol ?? '').trim().toUpperCase()}`;

/**
 * Deposits are keyed by account id, not by currency: a user can hold
 * several deposits in the same token, and each is hidden on its own.
 */
export const keyForDeposit = (accountId: string): string =>
    `deposit:${(accountId ?? '').trim()}`;

/**
 * Cards, stock positions and investment accounts are keyed per account for
 * the same reason deposits are: a client may hold two cards, two T-FUND
 * places, or the same ticker twice, and each is put away on its own.
 */
export const keyForCard = (accountId: string): string =>
    `card:${(accountId ?? '').trim()}`;

export const keyForStock = (accountId: string): string =>
    `stock:${(accountId ?? '').trim()}`;

export const keyForInvestment = (accountId: string): string =>
    `investment:${(accountId ?? '').trim()}`;

interface HiddenAccountsContextType {
    /** True after the initial AsyncStorage read finishes. Renders happen
     *  immediately — the override map is just empty until then. */
    isReady: boolean;

    /** Returns the explicit override for a key, or undefined if none set. */
    getOverride: (key: string) => VisibilityOverride | undefined;

    /** Sets the explicit override for a key. Pass null to clear. */
    setOverride: (key: string, override: VisibilityOverride | null) => void;

    /** Whether the account should appear on the Dashboard.
     *  defaultVisible = "would this row show if no override existed?".
     *  Callers decide what defaultVisible means (e.g. non-zero amount, or
     *  "always-on" tokens like USDT) — the context only resolves overrides. */
    isVisibleOnDashboard: (key: string, defaultVisible: boolean) => boolean;
}

const HiddenAccountsContext = createContext<HiddenAccountsContextType | undefined>(undefined);

export const HiddenAccountsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, isAuthenticated, isImpersonating } = useAuth();
    const userId = isAuthenticated ? user?.id ?? null : null;
    // Under "Login as user" the map is kept in memory only: the super-user's
    // device must not grow a visibility map for the client.
    const memoryOnly = isImpersonating === true;
    const [overrides, setOverrides] = useState<Record<string, VisibilityOverride>>({});
    const [isReady, setIsReady] = useState(false);
    // The user the map in memory belongs to; writes go under their key only.
    const ownerRef = useRef<string | null>(null);
    // Changes made while the user's map is still loading: laid over what
    // loads, instead of being overwritten by it.
    const changesWhileLoadingRef = useRef<Record<string, VisibilityOverride | null>>({});

    // Load the signed-in user's map whenever the user changes; the one-time
    // migration of the device-wide map happens here too.
    useEffect(() => {
        let cancelled = false;
        ownerRef.current = null;
        changesWhileLoadingRef.current = {};
        setOverrides({});
        setIsReady(false);
        if (!userId || memoryOnly) {
            setIsReady(true);
            return () => { cancelled = true; };
        }
        /** The loaded map with what was changed meanwhile laid over it, stored if anything was. */
        const settle = (loaded: Record<string, VisibilityOverride>) => {
            const changes = changesWhileLoadingRef.current;
            changesWhileLoadingRef.current = {};
            const merged = { ...loaded };
            for (const [k, v] of Object.entries(changes)) {
                if (v === null) { delete merged[k]; } else { merged[k] = v; }
            }
            ownerRef.current = userId;
            setOverrides(merged);
            return { merged, changed: Object.keys(changes).length > 0 };
        };
        (async () => {
            try {
                const own = parseOverrides(await AsyncStorage.getItem(storageKeyFor(userId)));
                if (cancelled) { return; }
                if (own) {
                    const { merged, changed } = settle(own);
                    if (changed) {
                        AsyncStorage.setItem(storageKeyFor(userId), JSON.stringify(merged)).catch(() => {});
                    }
                    return;
                }
                const [v2raw, v1raw] = await Promise.all([
                    AsyncStorage.getItem(LEGACY_KEY_V2),
                    AsyncStorage.getItem(LEGACY_KEY_V1),
                ]);
                if (cancelled) { return; }
                const legacy = parseOverrides(v2raw) ?? parseLegacyV1(v1raw);
                const { merged, changed } = settle(legacy ?? {});
                if (legacy || changed) {
                    // The map is now this user's. The device-wide one goes only
                    // once it is stored under the user — a failed write keeps it.
                    try {
                        await AsyncStorage.setItem(storageKeyFor(userId), JSON.stringify(merged));
                    } catch (e) {
                        console.warn('[HiddenAccounts] failed to store the migrated map:', e);
                        return;
                    }
                }
                if (v2raw) { AsyncStorage.removeItem(LEGACY_KEY_V2).catch(() => {}); }
                if (v1raw) { AsyncStorage.removeItem(LEGACY_KEY_V1).catch(() => {}); }
            } catch (e) {
                console.warn('[HiddenAccounts] failed to load AsyncStorage:', e);
            } finally {
                if (!cancelled) { setIsReady(true); }
            }
        })();
        return () => { cancelled = true; };
    }, [userId, memoryOnly]);

    // Write-through persistence under the map's owner — fire-and-forget,
    // never block UI. Signed out, nothing is stored.
    const persist = useCallback((next: Record<string, VisibilityOverride>) => {
        const owner = ownerRef.current;
        if (!owner) { return; }
        AsyncStorage.setItem(storageKeyFor(owner), JSON.stringify(next)).catch((e) => {
            console.warn('[HiddenAccounts] failed to persist AsyncStorage:', e);
        });
    }, []);

    const setOverride = useCallback(
        (key: string, override: VisibilityOverride | null) => {
            if (!ownerRef.current && userId && !memoryOnly) {
                // Still loading: remember it — a removal too, of a key the
                // empty map does not have yet — to lay over the loaded map.
                changesWhileLoadingRef.current = { ...changesWhileLoadingRef.current, [key]: override };
            }
            setOverrides((prev) => {
                const next = { ...prev };
                if (override === null) {
                    if (!(key in next)) { return prev; }
                    delete next[key];
                } else {
                    if (next[key] === override) { return prev; }
                    next[key] = override;
                }
                persist(next);
                return next;
            });
        },
        [persist, userId, memoryOnly],
    );

    const getOverride = useCallback(
        (key: string) => overrides[key],
        [overrides],
    );

    const isVisibleOnDashboard = useCallback(
        (key: string, defaultVisible: boolean) => {
            const ov = overrides[key];
            if (ov === 'hide') { return false; }
            if (ov === 'show') { return true; }
            return defaultVisible;
        },
        [overrides],
    );

    return (
        <HiddenAccountsContext.Provider value={{ isReady, getOverride, setOverride, isVisibleOnDashboard }}>
            {children}
        </HiddenAccountsContext.Provider>
    );
};

export const useHiddenAccounts = (): HiddenAccountsContextType => {
    const ctx = useContext(HiddenAccountsContext);
    if (!ctx) {
        throw new Error('useHiddenAccounts must be used within a HiddenAccountsProvider');
    }
    return ctx;
};
